<script setup lang="ts">
import { computed } from 'vue'
import FlatRouterLinkButton from '@/components/atoms/FlatRouterLinkButton.vue'
import BreadcrumbList from '@/components/molecules/BreadcrumbList.vue'
import { useRoute } from 'vue-router'
import { useAlert } from '@/libs/alertPortal'
import {
  useDeleteLineRichMenuMutation,
  useLineRichMenuIndexPageQuery,
} from './LineRichMenuIndexPage.generated'
import LineMessageSendingIndexPageSideMenu from '@/pages/ChatMessageSendingJobIndexPage/ChatMessageSendingJobIndexPageSideMenu.vue'
import CenteredPageContainer from '@/components/molecules/CenteredPageContainer.vue'
import TabsRoot from '@/components/molecules/Tabs/TabsRoot.vue'
import TabsList from '@/components/molecules/Tabs/TabsList.vue'
import TabsTrigger from '@/components/molecules/Tabs/TabsTrigger.vue'
import TabsContent from '@/components/molecules/Tabs/TabsContent.vue'
import LineRichMenuIndexPageTable from './LineRichMenuIndexPageTable.vue'
import { LineRichMenuStatus } from '@/types/schema.generated.ts'

const route = useRoute('LineRichMenuIndexPage')
const alert = useAlert()
const pageQuery = useLineRichMenuIndexPageQuery({
  fetchPolicy: 'no-cache',
})
const deleteMutation = useDeleteLineRichMenuMutation()

const handleClickDeleteButton = async (
  guid: string,
  label: string
): Promise<void> => {
  if (window.confirm(`本当に「${label}」を削除しますか？`)) {
    const result = await deleteMutation.mutate({
      input: guid,
    })

    if (result?.errors) {
      alert.error(`リッチメニュー「${label}」の削除に失敗しました`)
      return
    }

    alert.success(`リッチメニュー「${label}」を削除しました`)

    await pageQuery.refetch()
  }
}

const buildLineRichMenus = () =>
  pageQuery.result.value?.lineRichMenus.lineRichMenus ?? []
const lineRichMenus = computed(buildLineRichMenus)
const lineRichMenusPublished = computed(() =>
  buildLineRichMenus().filter(
    lineRichMenu => lineRichMenu.status === LineRichMenuStatus.Published
  )
)
const lineRichMenusUnpublished = computed(() =>
  buildLineRichMenus().filter(
    lineRichMenu => lineRichMenu.status === LineRichMenuStatus.Unpublished
  )
)
</script>

<template>
  <CenteredPageContainer :class="$style.page" wide>
    <div :class="[$style.page__main, $style.setting]">
      <LineMessageSendingIndexPageSideMenu />
      <div :class="$style.wrapper">
        <BreadcrumbList :links="route.meta.breadcrumbs" />
        <section :class="$style.container">
          <header :class="$style.header">
            <h1>LINEリッチメニュー</h1>
            <FlatRouterLinkButton
              variant="primary"
              size="medium"
              :to="{ name: 'LineRichMenuNewPage' }"
            >
              新規作成
            </FlatRouterLinkButton>
          </header>
          <hr :class="$style.hr" />
          <TabsRoot default-value="all">
            <TabsList>
              <TabsTrigger value="all">すべて</TabsTrigger>
              <TabsTrigger value="published">公開中</TabsTrigger>
              <TabsTrigger value="unpublished">非公開</TabsTrigger>
            </TabsList>
            <TabsContent value="all">
              <div :class="$style.containerInner" v-if="pageQuery.result.value">
                <LineRichMenuIndexPageTable
                  :lineRichMenus="lineRichMenus"
                  @handleClickDeleteButton="handleClickDeleteButton"
                />
              </div>
            </TabsContent>
            <TabsContent value="published">
              <div :class="$style.containerInner" v-if="pageQuery.result.value">
                <LineRichMenuIndexPageTable
                  :lineRichMenus="lineRichMenusPublished"
                  @handleClickDeleteButton="handleClickDeleteButton"
                />
              </div>
            </TabsContent>
            <TabsContent value="unpublished">
              <div :class="$style.containerInner" v-if="pageQuery.result.value">
                <LineRichMenuIndexPageTable
                  :lineRichMenus="lineRichMenusUnpublished"
                  @handleClickDeleteButton="handleClickDeleteButton"
                />
              </div>
            </TabsContent>
          </TabsRoot>
        </section>
      </div>
    </div>
  </CenteredPageContainer>
</template>

<style module lang="scss">
.wrapper {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0 auto;
  width: 100%;
}

.container {
  background-color: $color-surface-primary;
  padding: 16px;
  border-radius: 10px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.paginationWrapper {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.hr {
  border: none;
  border-top: 1px solid $color-border-low-emphasis;
  margin: 16px 0;
}

.containerInner {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 16px;
}

.page {
  padding-bottom: 80px;

  &__header {
    &:nth-child(n + 2) {
      margin-top: 40px;
    }
  }

  &__main {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.setting {
  display: grid;
  align-items: flex-start;
  gap: 40px;
  grid-template-columns: 302px 1fr;
  grid-template-rows: auto;
}
</style>
